// Human-readable display names for platform keys. Added 2026-09-03 as part
// of a UI/UX pass: before this, raw enum-style keys ("aws_s3", "ibm_cos",
// "digitalocean_spaces") were shown directly in the Sites table, the
// Connections table, the Queue page, and the Reports page — readable to
// whoever built the adapter, not to someone just trying to use the
// dashboard. clientDetail.ts used to keep its own local CLOUD_HOST_LABELS
// map (cloud hosts only, missing every content platform) — the same
// "hand-maintained list quietly goes stale" pattern that already caused two
// real bugs this session (see adapters/registry.ts's own header comment on
// listCloudHostPlatforms). Centralizing it here instead of leaving every
// view to define or omit its own copy.
//
// This is cosmetic-only, same as the old CLOUD_HOST_LABELS was — it plays no
// role in deciding what's a cloud host vs a content platform (that's the
// backend's `category` field, fetched fresh from /v1/connections every
// time) and nothing breaks if a new adapter is added without an entry here:
// platformLabel() falls back to a readable title-cased version of the raw key.
export const PLATFORM_LABELS: Record<string, string> = {
  wordpress: "WordPress",
  webflow: "Webflow CMS",
  wix_blog: "Wix Blog",
  cloudflare_pages: "Cloudflare Pages",
  netlify: "Netlify",
  github_pages: "GitHub Pages",
  aws_s3: "AWS S3",
  azure_storage: "Azure Storage",
  firebase_hosting: "Firebase Hosting",
  digitalocean_spaces: "DigitalOcean Spaces",
  vultr_object_storage: "Vultr Object Storage",
  wasabi: "Wasabi",
  linode: "Linode Object Storage",
  oracle_object_storage: "Oracle Cloud Object Storage",
  backblaze_b2: "Backblaze B2",
  bunny: "Bunny.net",
  ibm_cos: "IBM Cloud Object Storage",
  ovhcloud_object_storage: "OVHcloud Object Storage",
  upcloud_object_storage: "UpCloud Object Storage",
  scaleway_object_storage: "Scaleway Object Storage",
  facebook_pages: "Facebook Page",
  telegraph: "Telegraph",
  medium: "Medium",
  devto: "Dev.to",
  bluesky: "Bluesky",
  internet_archive: "Internet Archive",
  mastodon: "Mastodon",
  telegram: "Telegram",
  eventbrite: "Eventbrite",
  beehiiv: "Beehiiv",
  zenodo: "Zenodo",
  raindrop: "Raindrop.io",
};

export function platformLabel(platform: string): string {
  // Own keys only: a platform key from data such as "toString" or "__proto__" must not reach the prototype chain.
  const key = typeof platform === "string" ? platform : String(platform ?? "");
  if (Object.prototype.hasOwnProperty.call(PLATFORM_LABELS, key)) return PLATFORM_LABELS[key];
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function categoryLabel(category: string): string {
  return category === "cloud_host" ? "Cloud host" : category === "content_platform" ? "Content platform" : category;
}
